'use client'

import { cn } from '@quadralize/utils'
import { AnimatePresence, motion, type Variants } from 'framer-motion'
import * as React from 'react'

const ANIMATION_CONFIG = {
  STAGGER_DELAY: 0.05,
  SCALE_HIDDEN: 0.6,
  OFFSET_HIDDEN: 10,
} as const

// Reusing Button might be tricky if we want specific circular styles that override Button presets too much.
// But Button has 'icon' variant. Let's see.
// A FAB is distinct enough to be its own thing or wrap a primitive.
// Let's build it as a specialized button for animation control.

// --- Types ---

interface FabProps extends React.ComponentProps<typeof motion.button> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'default' | 'sm' | 'lg'
  icon?: React.ReactNode
  label?: string
  /**
   * If true, the button will be fixed to the bottom right of the screen.
   * Otherwise, it behaves like a normal element (useful for relative layouts).
   */
  fixed?: boolean
}

const fabVariants: Variants = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0, opacity: 0 },
  tap: { scale: 0.95 },
  hover: { scale: 1.05 },
}

const Fab = React.forwardRef<HTMLButtonElement, FabProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'default',
      icon,
      children,
      label,
      fixed = false,
      ...props
    },
    ref,
  ) => {
    // Styles
    const baseStyles =
      'inline-flex items-center justify-center rounded-full shadow-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-bg-base z-50'

    const variantStyles = {
      primary: 'bg-primary text-primary-fg hover:bg-primary/90',
      secondary: 'bg-secondary text-secondary-fg hover:bg-secondary/80',
      ghost: 'bg-bg-elevated text-text-primary hover:bg-bg-subtle border border-border-default', // Glass-like
      danger: 'bg-danger text-danger-fg hover:bg-danger/90',
    }

    const sizeStyles = {
      default: 'h-14 w-14', // 56px
      sm: 'h-10 w-10', // 40px
      lg: 'h-16 w-16', // 64px
    }

    const fixedStyles = fixed ? 'fixed bottom-6 right-6' : ''

    const content = (
      <motion.button
        ref={ref}
        layout
        variants={fabVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        whileTap="tap"
        whileHover="hover"
        className={cn(baseStyles, variantStyles[variant], sizeStyles[size], fixedStyles, className)}
        {...props}
      >
        {(icon as React.ReactNode) || (children as React.ReactNode)}
        {label && <span className="sr-only">{label}</span>}
      </motion.button>
    )

    // Tooltip logic could go here if we had a Tooltip component ready to wrap specific animated triggers.
    // For now, simple title or aria-label is enough, or custom tooltip handling in FabAction.
    return content
  },
)
Fab.displayName = 'Fab'

// --- Fab Group / Speed Dial ---

interface FabGroupContextValue {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  direction: 'up' | 'down' | 'left' | 'right'
}

const FabGroupContext = React.createContext<FabGroupContextValue | null>(null)

interface FabGroupProps {
  children: React.ReactNode
  direction?: 'up' | 'down' | 'left' | 'right'
  trigger: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const FabGroup = ({
  children,
  direction = 'up',
  trigger,
  open: controlledOpen,
  onOpenChange,
}: FabGroupProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const groupRef = React.useRef<HTMLDivElement>(null)

  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  const handleOpenChange = React.useCallback(
    (val: boolean) => {
      if (!isControlled) setInternalOpen(val)
      onOpenChange?.(val)
    },
    [isControlled, onOpenChange],
  )

  // Determine flex direction based on `direction` prop
  const containerDirection =
    direction === 'up'
      ? 'flex-col-reverse'
      : direction === 'down'
        ? 'flex-col'
        : direction === 'left'
          ? 'flex-row-reverse'
          : 'flex-row'

  const alignment = direction === 'up' || direction === 'down' ? 'items-center' : 'items-center'

  // Clone the trigger and add our handlers
  const enhancedTrigger = React.isValidElement(trigger)
    ? React.cloneElement(trigger as React.ReactElement<Record<string, unknown>>, {
        onClick: (e: React.MouseEvent) => {
          const originalOnClick =
            React.isValidElement(trigger) &&
            typeof (trigger as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>)
              .props.onClick === 'function'
              ? (trigger as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>).props
                  .onClick
              : undefined
          originalOnClick?.(e)
          handleOpenChange(!isOpen)
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          const originalOnKeyDown =
            React.isValidElement(trigger) &&
            typeof (trigger as React.ReactElement<{ onKeyDown?: (e: React.KeyboardEvent) => void }>)
              .props.onKeyDown === 'function'
              ? (trigger as React.ReactElement<{ onKeyDown?: (e: React.KeyboardEvent) => void }>)
                  .props.onKeyDown
              : undefined
          originalOnKeyDown?.(e)
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleOpenChange(!isOpen)
          }
        },
      })
    : trigger

  // Click outside to close
  React.useEffect(() => {
    if (!isOpen) return

    const handler = (e: MouseEvent) => {
      if (groupRef.current && !groupRef.current.contains(e.target as Node)) {
        handleOpenChange(false)
      }
    }

    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, handleOpenChange])

  return (
    <FabGroupContext.Provider value={{ isOpen: !!isOpen, setIsOpen: handleOpenChange, direction }}>
      <div ref={groupRef} className={cn('flex gap-4', containerDirection, alignment)}>
        {enhancedTrigger}
        <div className={cn('flex gap-3', containerDirection, alignment, 'pointer-events-none')}>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className={cn('flex gap-3 pointer-events-auto', containerDirection, alignment)}
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: ANIMATION_CONFIG.STAGGER_DELAY,
                      staggerDirection: direction === 'up' || direction === 'left' ? 1 : -1,
                    },
                  },
                }}
              >
                {children}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </FabGroupContext.Provider>
  )
}
FabGroup.displayName = 'FabGroup'

// --- Fab Action ---

interface FabActionProps extends FabProps {
  label?: string
  onClick?: () => void
}

const FabAction = React.forwardRef<HTMLButtonElement, FabActionProps>(
  ({ className, label, ...props }, ref) => {
    const { direction } = React.useContext(FabGroupContext) || { direction: 'up' }

    // Label positioning
    // Used to align label based on direction
    const labelDirection = direction === 'up' || direction === 'down' ? 'side' : 'vertical'

    const Label = label ? (
      <motion.div
        initial={{ opacity: 0, x: ANIMATION_CONFIG.OFFSET_HIDDEN }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: ANIMATION_CONFIG.OFFSET_HIDDEN }}
        className={cn(
          'px-2 py-1 bg-bg-elevated text-text-primary text-xs font-medium rounded shadow-md border border-border-default whitespace-nowrap absolute',
          labelDirection === 'side' ? 'right-[120%]' : 'bottom-[120%]',
        )}
      >
        {label}
      </motion.div>
    ) : null

    const actionVariants = {
      hidden: {
        opacity: 0,
        scale: ANIMATION_CONFIG.SCALE_HIDDEN,
        y: ANIMATION_CONFIG.OFFSET_HIDDEN,
      },
      visible: { opacity: 1, scale: 1, y: 0 },
    }

    return (
      <motion.div variants={actionVariants} className="relative flex items-center justify-center">
        {Label}
        <Fab ref={ref} size="sm" className={cn('z-40', className)} {...props} />
      </motion.div>
    )
  },
)
FabAction.displayName = 'FabAction'

export { Fab, FabAction, FabGroup }
